<template>
  <div class="participant-response-query-details">
    <div class="mb-16 query-back-btn-container d-flex justify-content-between p-7 fs-12">
      <span>Query ID {{ queryId | slice5 }}</span>
      <span @click="$emit('back')" class="back-to-queries-btn">Back</span>
    </div>
    <template v-if="!closed && !user.readOnly">
      <div class="d-flex" v-if="!isReplying">
        <button class="participant-response-query-reply-btn ml-16 mr-16" id="reply-query-button" :class="isButtonDisabled ? 'disable-button' : ''" @click="onReplyQuery" v-if="isReplyQueryVisible">Reply</button>
        <b-popover v-if="isButtonDisabled" target="reply-query-button" triggers="hover" placement="bottomright"> You cannot reply to queries on locked activities. </b-popover>
        <button class="participant-response-query-close-btn" id="close-query-button" :class="isButtonDisabled || isReopenCloseQueryButtonDisabled ? 'disable-button' : ''" @click="onCloseQuery" v-if="userRoles.canCraAccess || userRoles.canDmAccess || userRoles.canPharmacovigilanceAccess || userRoles.canCallCenterAccess || userRoles.canCraUBAccess">Close Query</button>
        <b-popover v-if="isButtonDisabled" target="close-query-button" triggers="hover" placement="bottomleft"> You cannot close queries on locked activities.</b-popover>
        <b-popover v-if="isReopenCloseQueryButtonDisabled" target="close-query-button" triggers="hover" placement="bottomleft"> You cannot close queries that were <br />raised by a different role. </b-popover>
      </div>
      <div v-else>
        <CommentForm saveBtnText="Post Reply" @submit="onReplyQueryFormSubmit" @cancel="onReplyQueryFormCancel" :replyQuery="true" />
      </div>
    </template>
    <template v-else>
      <div>
        <button class="participant-response-query-reopen-btn ml-16" id="reopen-query-button" :class="isButtonDisabled || isReopenCloseQueryButtonDisabled ? 'disable-button' : ''" @click="onReopenQuery" v-if="userRoles.canCraAccess || userRoles.canDmAccess || userRoles.canPharmacovigilanceAccess || userRoles.canCallCenterAccess || userRoles.canCraUBAccess">Reopen Query</button>
        <b-popover v-if="isButtonDisabled" target="reopen-query-button" triggers="hover" placement="bottomleft"> You cannot reopen queries on locked activities. </b-popover>
        <b-popover v-if="isReopenCloseQueryButtonDisabled" target="reopen-query-button" triggers="hover" placement="bottomleft"> You cannot reopen queries raised by a <br />different role. </b-popover>
      </div>
    </template>
    <ParticipantActivityResponseQueryCommentItem v-for="comment in comments" :key="comment.id" :comment="comment" @displayDetailedView="displayDetailedView" />

    <ConfirmationModal ref="closeConfirmation" text="Are you sure you want to close this query?" yesBtnTxt="Yes, close query" noBtnTxt="No, do not close query" @confirmed="onCloseQueryConfirmed" />

    <ConfirmationModal ref="reopenConfirmation" text="Are you sure you want to reopen this query?" yesBtnTxt="Yes, reopen query" noBtnTxt="No, do not reopen query" @confirmed="onReopenQueryConfirmed" />
  </div>
</template>

<script>
import ParticipantActivityResponseQueryCommentItem from "@/components/participants/activity-response/participant-activity-response-query-comment-item";
import ConfirmationModal from "@/components/modals/confirmation-modal";

import CommentForm from "@/components/form/edc-form-queries/form-queries-comment-form";
import localStorageService from "@/services/localStorage";
import { CRA, CraUB } from "@/constants/authRoles";

export default {
  name: "participant-response-query-details",
  props: {
    closed: Boolean,
    comments: {
      type: Array
    },
    queryId: {
      type: String
    },
    selectedQuestionId: {},
    userRoles: {},
    lockStatus: String
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    canCraAccess() {
      this.$acl.change(`${CRA}`);
      return this.$acl.check("userAccess");
    },
    canCraUBAccess() {
      this.$acl.change(`${CraUB}`);
      return this.$acl.check("userAccess");
    },
    isReplyQueryVisible() {
      const { canCraAccess, canPiAccess, canSubPiAccess, canStudyCoordinatorAccess, canHomeHealthAccess, canDmAccess, canPharmacovigilanceAccess, canCallCenterAccess, canCraUBAccess } = this.userRoles;

      return canHomeHealthAccess || canPiAccess || canStudyCoordinatorAccess || canSubPiAccess || canCraAccess || canPharmacovigilanceAccess || canDmAccess || canCallCenterAccess || canCraUBAccess;
    },
    isButtonDisabled() {
      let userObj = localStorageService.getItem("userObj");
      let userRole = userObj.userRole.toLowerCase();
      const valuesToCheck = ["activity_response_query.close_locked", "activity_response_query.reopen_locked", "activity_response_query.reply_locked"];
      const restrictionsArray = userObj.restrictions.split(" ");
      const containsValue = valuesToCheck.some(value => restrictionsArray.includes(value));
      if (containsValue && this.lockStatus.toLowerCase() === "locked") {
          return true;
      }
      return false;
    },
    isReopenCloseQueryButtonDisabled() {
      let arrayLength = this.comments.length - 1;
      let userObj = localStorageService.getItem("userObj");
      let issuerRole = this.comments[arrayLength].issuer_role.toLowerCase();
      if (userObj.restrictions !== "" && this.lockStatus.toLowerCase() === "unlocked") {
        if(!(issuerRole === "cra" || issuerRole === "craub")) {
          return !!userObj.restrictions.includes("activity_response_query.close_assigned");
        }
      } else {
        return false;
      }
    }
  },
  components: {
    CommentForm,
    ParticipantActivityResponseQueryCommentItem,
    ConfirmationModal
  },
  filters: {
    slice5(val) {
      return val.slice(0, 5);
    }
  },
  data() {
    return {
      isReplying: false
    };
  },
  methods: {
    onReplyQuery() {
      if (this.isButtonDisabled) {
        return true;
      }
      this.isReplying = true;
      return false;
    },
    onCloseQuery() {
      if (this.isButtonDisabled || this.isReopenCloseQueryButtonDisabled) {
        return true;
      }
      this.$refs.closeConfirmation.show();
      return false;
    },
    onCloseQueryConfirmed() {
      this.isReplying = false;
      const queryDetails = {
        queryId: this.queryId
      };
      this.$emit("closeQuery", queryDetails);
    },
    onReplyQueryFormSubmit(queryText) {
      this.isReplying = false;
      const queryDetails = {
        queryId: this.queryId,
        queryText: queryText
      };
      this.$emit("replyQuery", queryDetails);
    },
    onReplyQueryFormCancel() {
      this.isReplying = false;
    },
    displayDetailedView(query) {
      this.$emit("displayDetailedView", query);
    },
    onReopenQuery() {
      if (this.isButtonDisabled || this.isReopenCloseQueryButtonDisabled) {
        return true;
      }
      this.$refs.reopenConfirmation.show();
      return false;
    },
    onReopenQueryConfirmed() {
      const queryDetails = {
        queryId: this.queryId
      };
      this.$emit("reopenQuery", queryDetails);
    }
  }
};
</script>

<style scoped lang="scss">
.participant-response-query-reply-btn {
  width: 85px;
  height: 27.5px;
  border-radius: 3px;
  border: solid 1px #d7dfeb;
  background-color: #ffffff;
  color: #4c8ce4;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
  cursor: pointer;
}

.participant-response-query-close-btn {
  width: auto !important;
  height: 27.5px;
  border-radius: 3px;
  border: solid 1px #d7dfeb;
  background-color: #ffffff;
  padding: 2px 8px !important;
  color: #4c8ce4;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
  cursor: pointer;
}

.participant-response-query-reopen-btn {
  width: 120px;
  height: 27.5px;
  border-radius: 3px;
  border: solid 1px #d7dfeb;
  background-color: #ffffff;
  color: #4c8ce4;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
  cursor: pointer;
}

.query-back-btn-container {
  background-color: rgba(211, 223, 228, 0.5);
}

.back-to-queries-btn {
  cursor: pointer;
  text-decoration: underline;
}
.disable-button {
  opacity: 0.3;
}
</style>
